/**
 * Per-device activity counters and how to fold them back into per-day totals.
 *
 * Counters are the one thing last-write-wins gets badly wrong: ten reviews on
 * the phone and five on the laptop must total fifteen, but LWW on a shared
 * `"2026-07-29"` row would keep whichever arrived last and throw the rest
 * away. So each device owns a private bucket, `"2026-07-29#<deviceId>"`, that
 * only it ever increments — no two devices write the same record, so there is
 * nothing to resolve — and the buckets are summed on read.
 *
 * Pure, like `srs.ts`; the store is `hooks/useActivity.ts`.
 */

/**
 * What happened on one calendar day. `learned` counts words met for the very
 * first time — that's what the daily goal is about — while `reviews` counts
 * every answer, including repeats of the same word.
 */
export interface DayActivity {
  reviews: number;
  learned: number;
}

/** Totals per calendar day, `YYYY-MM-DD`. What the UI consumes. */
export type ActivityMap = Record<string, DayActivity>;

/** As stored and synced: one row per day *per device*. */
export type RawActivityMap = Record<string, DayActivity>;

export const EMPTY_DAY: DayActivity = { reviews: 0, learned: 0 };

const SEP = "#";

export function activityKey(day: string, deviceId: string): string {
  return `${day}${SEP}${deviceId}`;
}

/** The calendar day a bucket belongs to. Tolerates un-suffixed legacy keys. */
export function activityDay(key: string): string {
  const at = key.indexOf(SEP);
  return at < 0 ? key : key.slice(0, at);
}

function isEmpty(entry: DayActivity | undefined): boolean {
  return entry == null || (entry.reviews === 0 && entry.learned === 0);
}

/** Totals for a single day, without folding the whole map. */
export function sumDay(raw: RawActivityMap, day: string): DayActivity {
  let reviews = 0;
  let learned = 0;
  for (const [key, entry] of Object.entries(raw)) {
    if (activityDay(key) !== day) continue;
    reviews += entry.reviews;
    learned += entry.learned;
  }
  return { reviews, learned };
}

/** Collapse every device's buckets into one entry per calendar day. */
export function foldActivity(raw: RawActivityMap): ActivityMap {
  const out: ActivityMap = {};
  for (const [key, entry] of Object.entries(raw)) {
    const day = activityDay(key);
    const cell = out[day];
    if (cell == null) {
      out[day] = { reviews: entry.reviews, learned: entry.learned };
    } else {
      cell.reviews += entry.reviews;
      cell.learned += entry.learned;
    }
  }
  return out;
}

/**
 * The days that count towards a streak.
 *
 * All-zero buckets are excluded deliberately. A device that has synced but not
 * studied can hold a `{reviews: 0, learned: 0}` row, and treating that as a
 * study day would invent streak days the learner never earned.
 */
export function activityDayKeys(raw: RawActivityMap): Set<string> {
  const days = new Set<string>();
  for (const [key, entry] of Object.entries(raw)) {
    if (isEmpty(entry)) continue;
    days.add(activityDay(key));
  }
  return days;
}
